import type { ErrorEvent } from '@sentry/react';

const APP_BUNDLE_PATH = '/static/js/';
const ANALYTICS_TRACKING_PATH = '/api/analytics/v2/tracking/track';

/**
 * Detects SyntaxErrors raised by scripts the native mobile host evaluates
 * inside the webview (miniapp SDK bridge responses, injected instrumentation).
 *
 * WebKit reports parse failures of such sourceless scripts via window.onerror
 * with the page URL as the only "frame" and no stack, so they are
 * indistinguishable from app errors by message alone. Errors thrown by our
 * own code always carry at least one frame from the app bundles under
 * /static/js/. See SMB-373 for the full investigation.
 */
export const isInjectedScriptSyntaxError = (event: ErrorEvent): boolean => {
  const exception = event.exception?.values?.[0];

  if (exception?.type !== 'SyntaxError' || exception.mechanism?.handled) {
    return false;
  }

  const frames = exception.stacktrace?.frames ?? [];

  return !frames.some(frame => frame.filename?.includes(APP_BUNDLE_PATH));
};

interface AxiosErrorContext {
  config?: { url?: string };
}

/**
 * Detects unhandled 401s from the HqO analytics tracking endpoint.
 *
 * @hqo/web-tracking@2.0.x fires its analytics POST as fire-and-forget — the
 * axios call carries no `.catch`, and `track()` never returns the promise — so
 * a 401 (an expired token on deeplinked receipt/cancel flows) surfaces as an
 * unhandled promise rejection that we cannot catch at the call site. It is pure
 * noise: 0 users impacted, no functional effect. Fixed upstream in v5
 * (try/catch + fresh-token callback); drop it here until we migrate.
 *
 * Sentry's extraErrorDataIntegration serialises the failing axios error onto
 * the "Error" context, so we match on the request URL. See SMB-398.
 */
export const isUnhandledAnalyticsTracking401 = (event: ErrorEvent): boolean => {
  const exception = event.exception?.values?.[0];

  if (exception?.mechanism?.handled !== false || !exception.value?.includes('status code 401')) {
    return false;
  }

  const errorContext = event.contexts?.Error as AxiosErrorContext | undefined;

  return (errorContext?.config?.url ?? '').includes(ANALYTICS_TRACKING_PATH);
};

/**
 * Sentry `beforeSend` hook: drops known-noise events before they are reported.
 */
export const dropSentryNoise = (event: ErrorEvent): ErrorEvent | null =>
  isInjectedScriptSyntaxError(event) || isUnhandledAnalyticsTracking401(event) ? null : event;
